import { Connection } from '@solana/web3.js'
import RpcPing from './RpcPing'
import Tps from './Tps'
import { sumBy } from 'lodash'
import store from 'stores/store'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import useInterval from 'hooks/useInterval'
import Tooltip from '@components/shared/Tooltip'
import { milliseconds } from 'date-fns'

export const tpsAlertThreshold = 1300
export const tpsWarningThreshold = 1000
export const rpcAlertThreshold = 1000
export const rpcWarningThreshold = 2000

const getRecentPerformance = async (
  connection: Connection,
  setTps: (x: number) => void,
) => {
  try {
    const samples = 2
    const response = await connection.getRecentPerformanceSamples(samples)
    const totalSecs = sumBy(response, 'samplePeriodSecs')
    const totalTransactions = sumBy(response, 'numTransactions')
    const tps = totalTransactions / totalSecs

    setTps(tps)
  } catch (error) {
    console.warn('Unable to fetch TPS', error)
  }
}

const getPingTime = async (
  connection: Connection,
  setRpcPing: (x: number) => void,
) => {
  const startTime = Date.now()
  try {
    await connection.getSlot()

    const endTime = Date.now()
    const pingTime = endTime - startTime
    setRpcPing(pingTime)
  } catch (error) {
    console.error('Error pinging the RPC:', error)
    return null
  }
}

const getOverallStatus = (tps: number, rpcPing: number) => {
  if (tps < tpsWarningThreshold) {
    return 'severely-degraded'
  } else if (tps < tpsAlertThreshold || rpcPing > rpcWarningThreshold) {
    return 'degraded'
  } else return 'operational'
}

const Status = () => {
  const { t } = useTranslation('status-bar')
  const connection = store((s) => s.connection)
  const [tps, setTps] = useState(0)
  const [rpcPing, setRpcPing] = useState(0)

  useEffect(() => {
    void getPingTime(connection, setRpcPing)
    void getRecentPerformance(connection, setTps)
  }, [connection])

  useInterval(
    () => {
      void getPingTime(connection, setRpcPing)
      void getRecentPerformance(connection, setTps)
    },
    milliseconds({ minutes: 5 }),
  )

  const platformHealth = useMemo(() => {
    return getOverallStatus(tps, rpcPing)
  }, [tps, rpcPing])

  const dotColor =
    platformHealth === 'severely-degraded'
      ? 'bg-th-error'
      : platformHealth === 'degraded'
        ? 'bg-th-warning'
        : 'bg-th-success'

  return (
    <div className="flex items-center">
      <Tooltip
        content={
          <div className="space-y-2">
            <div>
              <p className="mb-0.5">{t('tooltip.solana-tps')}</p>
              <Tps tps={tps} />
            </div>
            <div>
              <p className="mb-0.5">{t('tooltip.rpc-ping')}</p>
              <RpcPing rpcPing={rpcPing} />
            </div>
          </div>
        }
        placement="top-start"
      >
        <div className="flex items-center">
          <div className="relative mr-1 size-3">
            <div
              className={`absolute left-0.5 top-0.5 size-2 animate-pulse rounded-full ${dotColor}`}
            />
            <div
              className={`absolute size-3 rounded-full opacity-40 ${dotColor}`}
            />
          </div>
          <p className="tooltip-underline text-xs leading-none">
            {t(`status.${platformHealth}`)}
          </p>
        </div>
      </Tooltip>
    </div>
  )
}

export default Status
